import { Link } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import {
    EditButton,
    Header,
    LinkButton,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '@frontend/components/elements/common';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';

interface IServiceGroupSettingsListProps {
    serviceId: string;
}

interface IServiceGroupSetting {
    endDate: string | null;
    group: {
        id: string;
        name: string;
    };
    id: string;
    startDate: string;
}

export function ServiceGroupSettingsList(props: IServiceGroupSettingsListProps) {
    const { serviceId } = props;
    const [serviceGroupSettings, setServiceGroupSettings] = useState<IServiceGroupSetting[]>([]);

    assertHasAuthorization(ManagerAuthorization.serviceRightsGroups);

    useEffect(() => {
        loadServiceGroupSettings(serviceId);
    }, [serviceId]);

    return (
        <>
            <Header
                actionButtons={[
                    <LinkButton
                        icon="icon icon-add"
                        key="add-button"
                        to={`/serviceGroupSettings/add?serviceId=${serviceId}`}
                    >
                        {translateKey('common.addItem', translateKey('groupSettings.serviceGroupSettings'))}
                    </LinkButton>,
                ]}
                content={translateKey('groupSettings.serviceGroupSettings')}
            />

            <div className="table">
                <TableHead>
                    <TableRow>
                        <TableHeader>{translateKey('groups.groupName')} </TableHeader>
                        <TableHeader>{translateKey('common.startDate')} </TableHeader>
                        <TableHeader>{translateKey('common.endDate')} </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {serviceGroupSettings.map((serviceGroupSetting) => (
                        <TableRow key={serviceGroupSetting.id}>
                            <TableData>
                                {hasOneAuthorization(ManagerAuthorization.groupRead) ? (
                                    <Link to={`/groups/${serviceGroupSetting.group.id}`}>
                                        {serviceGroupSetting.group.name}
                                    </Link>
                                ) : (
                                    serviceGroupSetting.group.name
                                )}
                            </TableData>
                            <TableData>
                                <NullableFormattedDate value={serviceGroupSetting.startDate} />
                            </TableData>
                            <TableData>
                                <NullableFormattedDate value={serviceGroupSetting.endDate} />
                            </TableData>
                            <TableData align="right">
                                <EditButton
                                    to={`/serviceGroupSettings/${serviceGroupSetting.id}?serviceId=${serviceId}`}
                                />
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </div>
        </>
    );

    async function loadServiceGroupSettings(id: string) {
        const url = `/api/serviceGroupSettings?serviceId=${id}`;

        const results = await fetchJson(url, 'GET');
        setServiceGroupSettings(results.json.items);
    }
}
